import React, { useMemo, useEffect, useRef } from 'react';
import { Search, X, User, CheckCircle, FileText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export type SearchResultItem =
  | { type: 'customer'; id: string; title: string; subtitle?: string; customer: any }
  | { type: 'task'; id: string; title: string; subtitle?: string; task: any };

type Props = {
  open: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  customers: any[];
  tasks: any[];
  onSelectCustomer: (c: any) => void;
  onSelectTask?: (t: any) => void;
  onNavigate: (view: string) => void;
};

const APP_TITLE = 'AgenturCRM';

export function GlobalSearch({
  open,
  onClose,
  query,
  onQueryChange,
  customers,
  tasks,
  onSelectCustomer,
  onSelectTask,
  onNavigate,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme } = useTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    if (open) {
      onQueryChange('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const results = useMemo<SearchResultItem[]>(() => {
    const q = (query || '').trim().toLowerCase();
    if (q.length < 2) return [];
    const items: SearchResultItem[] = [];
    customers.forEach((c) => {
      const name = `${c.vorname || ''} ${c.name || ''}`.trim() || c.name || '';
      const vsnr = c.verträge?.[0]?.vsnr || '';
      const match = name.toLowerCase().includes(q) || (vsnr && String(vsnr).toLowerCase().includes(q));
      if (match) items.push({ type: 'customer', id: c.id, title: name, subtitle: vsnr ? `VSNR: ${vsnr}` : undefined, customer: c });
    });
    tasks.forEach((t) => {
      const title = t.title || t.description || '';
      const match = title.toLowerCase().includes(q);
      if (match) items.push({ type: 'task', id: t.id, title: title.slice(0, 60), subtitle: t.status || 'Aufgabe', task: t });
    });
    return items.slice(0, 15);
  }, [query, customers, tasks]);

  const handleSelect = (item: SearchResultItem) => {
    if (item.type === 'customer') {
      onSelectCustomer(item.customer);
      onNavigate('customers');
    } else if (item.type === 'task' && onSelectTask) {
      onSelectTask(item.task);
      onNavigate('tasks');
    }
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-xl rounded-2xl shadow-2xl border overflow-hidden ${
          isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
          <Search size={20} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Kunden, VSNR, Aufgaben suchen…"
            className={`flex-1 bg-transparent outline-none text-sm ${
              isDark ? 'text-white placeholder-white/50' : 'text-slate-900 placeholder-slate-400'
            }`}
          />
          <button
            type="button"
            onClick={onClose}
            className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
          >
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {query.trim().length < 2 ? 'Mindestens 2 Zeichen eingeben.' : 'Keine Treffer.'}
            </div>
          ) : (
            results.map((item) => (
              <button
                key={`${item.type}-${item.id}`}
                type="button"
                onClick={() => handleSelect(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b last:border-b-0 ${
                  isDark ? 'border-slate-700 hover:bg-slate-700/60' : 'border-slate-100 hover:bg-slate-50'
                }`}
              >
                {item.type === 'customer' ? (
                  <User size={18} className={isDark ? 'text-cyan-400' : 'text-cyan-600'} />
                ) : (
                  <CheckCircle size={18} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</div>
                  {item.subtitle && (
                    <div className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.subtitle}</div>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
        <div className={`px-4 py-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          <kbd className="px-1.5 py-0.5 rounded border border-current">Esc</kbd> schließen · <kbd className="px-1.5 py-0.5 rounded border border-current">Strg+K</kbd> Suche
        </div>
      </div>
    </div>
  );
}
